/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import moment from "moment";
import { getData } from "../../Services/getData";
import { urlEnum, localUrlEnum } from "../../Utils/urlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";

type ClientVehicle = {
  id: number;
  vin: string;
  brand_r?: { nume: string };
  model_r?: { nume: string };
  status_vehicul_r?: { nume: string; culoare?: string };
  an_fabricatie: number | null;
  kilometri: number | null;
  pret_vanzare_euro: number | null;
  data_vanzare: string | null;
};

interface ClientVehiclesPanelProps {
  clientId: number;
}

export default function ClientVehiclesPanel({ clientId }: ClientVehiclesPanelProps) {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<ClientVehicle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    getData(`${urlEnum.clientVehicles}/${clientId}`)
      .then((res: any) => {
        const data = res?.data;
        const list = data?.vehicles ?? data?.data ?? data;
        setVehicles(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setVehicles([]);
      })
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!vehicles?.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={3}
        gap={1}
      >
        <DirectionsCarIcon style={{ fontSize: 40, color: "#d1d5db" }} />
        <Typography
          variant="body2"
          color="text.disabled"
          style={{ fontStyle: "italic" }}
        >
          {Vocabulary.noVehicles}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>{Vocabulary.brand}</strong>
            </TableCell>
            <TableCell>
              <strong>{Vocabulary.model}</strong>
            </TableCell>
            <TableCell>
              <strong>{Vocabulary.vin}</strong>
            </TableCell>
            <TableCell>
              <strong>{Vocabulary.year}</strong>
            </TableCell>
            <TableCell>
              <strong>{Vocabulary.status}</strong>
            </TableCell>
            <TableCell>
              <strong>{Vocabulary.sellingPriceEuro}</strong>
            </TableCell>
            <TableCell>
              <strong>{Vocabulary.sellingData}</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vehicles?.map((v) => (
            <Tooltip key={v.id} title={Vocabulary.clickToSeeAllVehicles} placement="top">
              <TableRow
                hover
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(
                    `${localUrlEnum.vehicles}/vehicleDetails/${v.id}`
                  )
                }
              >
                <TableCell>{v.brand_r?.nume || "-"}</TableCell>
                <TableCell>{v.model_r?.nume || "-"}</TableCell>
                <TableCell
                  style={{ fontFamily: "monospace", fontSize: 12 }}
                >
                  {v.vin || "-"}
                </TableCell>
                <TableCell>{v.an_fabricatie || "-"}</TableCell>
                <TableCell>
                  {v.status_vehicul_r ? (
                    <Chip
                      label={v.status_vehicul_r.nume}
                      size="small"
                      style={{
                        backgroundColor:
                          v.status_vehicul_r.culoare || "#e0e0e0",
                        color: "#fff",
                        fontSize: 11,
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  {v.pret_vanzare_euro
                    ? `€${v.pret_vanzare_euro.toLocaleString()}`
                    : "-"}
                </TableCell>
                <TableCell>
                  {v.data_vanzare
                    ? moment(v.data_vanzare).format("DD.MM.YYYY")
                    : "-"}
                </TableCell>
              </TableRow>
            </Tooltip>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
